import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field, FieldLabel } from '@/components/ui/field'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useTheme } from '@/lib/contexts/ThemeContext'
import { HugeiconsIcon } from "@hugeicons/react"
import { Copy01Icon, Loading01Icon, Tick01Icon } from "@hugeicons/core-free-icons"
import { register, googleAuth, type AuthResponse, type GoogleResponse } from '@/lib/api/auth'
import { toast } from 'sonner'
import { track } from '@/lib/utils/track'
import { markOtpConfirmed } from '@/lib/utils/authState'
import { captureAcquisitionContext, getAcquisitionContext } from '@/lib/utils/acquisitionContext'
import { executeRecaptcha } from '@/lib/utils/recaptcha'
import { newHandoffCode, registerHandoff } from '@/lib/utils/agentHandoff'
import HumblyticsLogo from '@/assets/humblytics-web-app-logo.svg'
import claudeMark from '@/assets/logos/agents/claude.svg'
import chatgptMark from '@/assets/logos/agents/chatgpt.svg'
import codexMark from '@/assets/logos/agents/codex.svg'
import cursorMark from '@/assets/logos/agents/cursor.svg'


declare global {
  interface Window {
    googleauth: (response: GoogleResponse & { select_by?: string }) => Promise<void>
    google?: any
  }
}

type AuthMethod = 'email' | 'google'
type AuthMode = 'signup' | 'signin'

const AUTH_METHOD_KEY = 'humblytics_last_auth_method'
const ONE_TAP_SESSION_KEY = 'humblytics_one_tap_prompted_v1'

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.348 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  )
}

// "Humblytics Sign-in" OAuth client in the humblytics-prod Google Cloud project. One client for
// every environment: its authorized JavaScript origins cover app, beta and https://localhost:5173.
// The backend must verify ID tokens against the same ID (GOOGLE_CLIENT in services/auth.js).
function getGoogleClientId() {
  return '198530242508-t27l3kkdprkukkgfjtai1ck8p44oru51.apps.googleusercontent.com'
}

function isOneTapSelectBy(selectBy?: string): boolean {
  if (!selectBy) return false
  return selectBy.includes('1tap') || selectBy.includes('auto') || selectBy === 'user_1tap'
}

export default function Auth() {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [googleReady, setGoogleReady] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { setToken, token } = useAuth()
  const { setTheme } = useTheme()
  const emailInputRef = useRef<HTMLInputElement>(null)
  const googleWrapRef = useRef<HTMLDivElement>(null)
  const googleInitializedRef = useRef(false)
  const oneTapPromptedRef = useRef(false)
  const lastAuthMethod = useRef<AuthMethod | null>(
    (localStorage.getItem(AUTH_METHOD_KEY) as AuthMethod) || null
  )

  const modeParam = searchParams.get('mode') as AuthMode | null
  const mode: AuthMode = modeParam === 'signin' ? 'signin' : 'signup'
  const isSignup = mode === 'signup'

  useEffect(() => {
    if (!modeParam) {
      const next = new URLSearchParams(searchParams)
      next.set('mode', 'signup')
      setSearchParams(next, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const setMode = (newMode: AuthMode) => {
    const next = new URLSearchParams(searchParams)
    next.set('mode', newMode)
    setSearchParams(next, { replace: true })
  }

  const from = (location.state as any)?.from?.pathname || searchParams.get('from') || '/dashboard'

  useEffect(() => {
    captureAcquisitionContext(from)
  }, [from])

  useEffect(() => {
    const prefillEmail = searchParams.get('email')
    if (prefillEmail) setEmail(prefillEmail)
  }, [searchParams])

  useEffect(() => {
    document.title = mode === 'signin' ? 'Sign In - Humblytics' : 'Create Account - Humblytics'
  }, [mode])

  useEffect(() => {
    track('auth_page_viewed', null, {
      ...getAcquisitionContext(),
      auth_flow: mode,
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const stored = localStorage.getItem('theme') as 'light' | 'dark' | null
    setTheme('light')
    return () => {
      if (stored && stored !== 'light') {
        setTheme(stored)
      }
    }
  }, [])

  useEffect(() => {
    if (token) {
      navigate(from, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoogleCredential = useCallback(async (response: GoogleResponse & { select_by?: string }) => {
    try {
      const result = await googleAuth(response.credential)
      if (result && 'authToken' in result) {
        const { authToken, isNew, noSites, hasSubscription } = result as AuthResponse
        if (authToken) {
          localStorage.setItem(AUTH_METHOD_KEY, 'google')
          const viaOneTap = isOneTapSelectBy(response.select_by)
          track(isNew ? 'signup_completed' : 'signin_completed', authToken, {
            ...getAcquisitionContext(),
            auth_method: viaOneTap ? 'google_one_tap' : 'google',
            google_select_by: response.select_by ?? null,
            is_new_user: isNew,
          })
          markOtpConfirmed()
          setToken(authToken)
          if (isNew) {
            navigate('/onboarding/start')
          } else if (!hasSubscription) {
            navigate('/dashboard')
          } else if (noSites) {
            navigate('/onboarding/add-site')
          } else {
            navigate(from, { replace: true })
          }
          return
        }
      }

      toast.error('Sign-in failed. Please try again.')
    } catch {
      toast.error('Sign-in failed. Please try again.')
    }
  }, [from, navigate, setToken])

  // Load GSI script once
  useEffect(() => {
    window.googleauth = handleGoogleCredential

    if (window.google?.accounts?.id) {
      setGoogleReady(true)
    } else {
      const existing = document.querySelector('script[src*="accounts.google.com/gsi/client"]')
      const script = existing ?? (() => {
        const s = document.createElement('script')
        s.src = 'https://accounts.google.com/gsi/client'
        s.async = true
        s.id = 'google-signin-script'
        document.body.appendChild(s)
        return s
      })()
      script.addEventListener('load', () => setGoogleReady(true), { once: true })
    }
  }, [handleGoogleCredential])

  // Initialize GSI once; render button whenever ready. One Tap is a separate prompt.
  useEffect(() => {
    if (!googleReady || !window.google?.accounts?.id) return

    if (!googleInitializedRef.current) {
      window.google.accounts.id.initialize({
        client_id: getGoogleClientId(),
        callback: (response: GoogleResponse & { select_by?: string }) => {
          void handleGoogleCredential(response)
        },
        auto_select: false,
        cancel_on_tap_outside: true,
        context: isSignup ? 'signup' : 'signin',
        itp_support: true,
      })
      googleInitializedRef.current = true
    }

    const buttonContainer = document.querySelector('.g_id_signin')
    if (buttonContainer) {
      buttonContainer.innerHTML = ''
      const width = Math.min(400, googleWrapRef.current?.offsetWidth || 320)
      window.google.accounts.id.renderButton(buttonContainer, {
        type: 'standard',
        shape: 'rectangular',
        theme: 'outline',
        text: 'continue_with',
        size: 'large',
        logo_alignment: 'center',
        width,
      })
    }
  }, [googleReady, handleGoogleCredential, isSignup])

  // One Tap — signup only, once per session, never when returning email users on sign-in
  useEffect(() => {
    if (!googleReady || !window.google?.accounts?.id) return
    if (!isSignup) return
    if (oneTapPromptedRef.current) return
    if (sessionStorage.getItem(ONE_TAP_SESSION_KEY) === '1') return
    // Don't fight a returning email user who bounced to signup via mode switch
    if (lastAuthMethod.current === 'email') return

    oneTapPromptedRef.current = true
    sessionStorage.setItem(ONE_TAP_SESSION_KEY, '1')

    try {
      window.google.accounts.id.prompt((notification: {
        isNotDisplayed: () => boolean
        isSkippedMoment: () => boolean
        isDismissedMoment: () => boolean
        getNotDisplayedReason?: () => string
        getDismissedReason?: () => string
      }) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          track('auth_one_tap_suppressed', null, {
            ...getAcquisitionContext(),
            reason: notification.getNotDisplayedReason?.() ?? 'skipped',
          })
          return
        }
        if (notification.isDismissedMoment()) {
          track('auth_one_tap_dismissed', null, {
            ...getAcquisitionContext(),
            reason: notification.getDismissedReason?.() ?? 'dismissed',
          })
        }
      })
      track('auth_one_tap_prompted', null, { ...getAcquisitionContext() })
    } catch {
      // GSI prompt can throw if FedCM / cooldown blocks — ignore
    }
  }, [googleReady, isSignup])

  useEffect(() => {
    if (mode === 'signin') {
      emailInputRef.current?.focus()
    }
  }, [mode])

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(value.trim())
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address')
      return
    }

    setIsLoading(true)
    const cleanEmail = email.trim().toLowerCase()

    try {
      const recaptchaSiteKey = import.meta.env.VITE_PUBLIC_RECAPTCHA_SITE_KEY || ''
      let recaptchaToken = ''
      try {
        recaptchaToken = await executeRecaptcha(recaptchaSiteKey, 'signup')
      } catch (recaptchaError) {
        console.error('reCAPTCHA error:', recaptchaError)
        toast.error('Security check failed. Please try again.')
        setIsLoading(false)
        return
      }

      const fallbackName = cleanEmail.split('@')[0] || 'Humblytics user'
      const codeRequested = await register(cleanEmail, fallbackName, recaptchaToken)
      if (!codeRequested) throw new Error('verification_code_request_failed')

      const isNewUser = typeof codeRequested === 'object' && 'codeSent' in codeRequested
        ? codeRequested.isNew
        : isSignup

      if (isSignup) {
        track('signup_initiated', null, {
          ...getAcquisitionContext(),
          auth_method: 'email',
          auth_flow: mode,
        })
      }
      localStorage.setItem(AUTH_METHOD_KEY, 'email')
      const verifyParams = new URLSearchParams({ email: cleanEmail, from, mode })
      if (isNewUser) {
        verifyParams.set('t', 'n')
        verifyParams.set('name', fallbackName)
      }
      navigate(`/verify?${verifyParams.toString()}`)
    } catch {
      if (isSignup) {
        track('signup_initiated', null, {
          ...getAcquisitionContext(),
          auth_method: 'email',
          auth_flow: mode,
          request_failed: true,
        })
      }
      localStorage.setItem(AUTH_METHOD_KEY, 'email')
      toast.error('Could not send a verification code. Please try again.')
      setIsLoading(false)
    }
  }

  const heading = isSignup
    ? {
        eyebrow: 'Get started',
        title: 'Create your Humblytics account.',
        body: 'Connect your site, Google and Meta ads, and Stripe. See which ads and pages make you money, then test changes.',
      }
    : {
        // No eyebrow: the headline already says sign in
        eyebrow: null,
        title: 'Welcome back. Your data’s ready.',
        body: 'Use Google or the email you signed up with.',
      }

  const headingBlock = (
    <div className="flex flex-col gap-2 text-left">
      {heading.eyebrow && (
        <p className="text-[13px] font-medium leading-5 text-muted-foreground">{heading.eyebrow}</p>
      )}
      <h1 className="max-w-[420px] text-balance text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground sm:text-[28px] sm:leading-[34px]">
        {heading.title}
      </h1>
      <p className="max-w-[420px] text-pretty text-sm leading-[22px] text-muted-foreground">{heading.body}</p>
    </div>
  )

  const conversionBlock = (
    <div className={`flex w-full flex-col gap-4 ${isSignup ? 'max-w-[380px]' : ''}`}>
      <div className="relative">
        {/* Badge sits inside the button's right edge (Paper); clicks pass through to Google */}
        {lastAuthMethod.current === 'google' && (
          <span className="pointer-events-none absolute right-2.5 top-3 z-10 rounded-[7px] bg-primary-tint px-[7px] py-0.5 text-[11px] leading-[14px] font-medium text-primary-strong ring-1 ring-inset ring-primary-tint-ring">
            Last used
          </span>
        )}
        <div ref={googleWrapRef} className="flex h-11 w-full min-w-0 items-center justify-center overflow-hidden">
          {googleReady ? (
            <div className="g_id_signin w-full" />
          ) : (
            <div
              aria-busy="true"
              aria-label="Loading Google sign-in"
              className="flex h-11 w-full items-center justify-center gap-2.5 rounded-lg bg-card text-sm font-medium text-foreground ring-1 ring-inset ring-input"
            >
              <GoogleMark />
              <span>Continue with Google</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3" aria-hidden="true">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs leading-4 text-muted-foreground">or email</span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <form onSubmit={handleEmailSubmit} className="flex flex-col gap-3 text-left">
        <Field className="gap-1.5">
          <div className="flex items-center gap-2">
            <FieldLabel htmlFor="email" className="text-[13px] font-medium leading-5 text-foreground">
              Email address
            </FieldLabel>
            {lastAuthMethod.current === 'email' && (
              <span className="rounded-[7px] bg-primary-tint px-[7px] py-0.5 text-[11px] leading-[14px] font-medium text-primary-strong ring-1 ring-inset ring-primary-tint-ring">
                Last used
              </span>
            )}
          </div>
          <Input
            id="email"
            ref={emailInputRef}
            placeholder="you@example.com"
            type="email"
            autoCapitalize="none"
            autoComplete="email"
            autoCorrect="off"
            disabled={isLoading}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="h-11 rounded-lg bg-card px-3 text-sm shadow-xs md:text-sm placeholder:text-neutral-400"
          />
        </Field>

        <Button type="submit" disabled={isLoading} className="h-11 w-full gap-2 rounded-lg px-4 text-sm font-medium shadow-none">
          {/* Always mounted: inserting this icon on submit crashed /auth via
              insertBefore when a third-party script reparented the label text
              node (JAVASCRIPT-SVELTEKIT-W0). */}
          <span className={isLoading ? undefined : 'hidden'} aria-hidden={!isLoading}>
            <HugeiconsIcon icon={Loading01Icon} size={18} className="animate-spin" />
          </span>
          <span>{isSignup ? 'Create account' : 'Send sign-in code'}</span>
        </Button>

        <div className="flex flex-col gap-1 text-xs leading-[18px] text-muted-foreground">
          {isSignup ? (
            <>
              <p>We’ll email you a six-digit code. No password needed.</p>
              <p>Free to start: 25,000 data points, no card.</p>
            </>
          ) : (
            <p>We&apos;ll email a 6-digit code, usually under a minute.</p>
          )}
        </div>
      </form>

      {/* Hairline splits the alternate path off the primary flow; legal sits last and quietest,
          on sign-up only (returning users agreed when they created the account). */}
      <div className="flex flex-col gap-2 border-t border-border pt-4 text-left">
        <p className="text-[13px] leading-5 text-muted-foreground">
          {isSignup ? 'Already have an account? ' : 'New here? '}
          <button
            type="button"
            onClick={() => setMode(isSignup ? 'signin' : 'signup')}
            className="font-medium text-foreground underline underline-offset-2 transition-colors hover:text-muted-foreground"
          >
            {isSignup ? 'Sign in' : 'Create an account'}
          </button>
        </p>
        {isSignup && (
        <p className="text-xs leading-[18px] text-muted-foreground">
          By continuing, you agree to our{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://humblytics.com/utilities/terms-of-service"
            className="underline transition-colors hover:text-foreground"
          >
            Terms
          </a>
          {' and '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://humblytics.com/utilities/privacy-policy"
            className="underline transition-colors hover:text-foreground"
          >
            Privacy
          </a>
          .
        </p>
        )}
      </div>
    </div>
  )

  return (
    <div className="relative flex min-h-svh flex-col bg-canvas text-foreground">
      <header className="px-4 pt-5 sm:px-8 sm:pt-7 lg:absolute lg:left-0 lg:top-0">
        <Link to="/" className="inline-flex items-center gap-2">
          <img src={HumblyticsLogo} alt="" className="size-5" />
          <span className="text-base font-medium leading-5 tracking-[-0.01em]">Humblytics</span>
        </Link>
      </header>

      <main
        className={`flex w-full flex-1 flex-col items-center justify-center px-4 sm:px-8 ${
          isSignup ? 'pb-8 pt-5 lg:py-16' : 'pb-8 pt-5 lg:py-12'
        }`}
      >
        {isSignup ? (
          <div className="surface-raised flex w-full max-w-[1040px] flex-col overflow-hidden lg:flex-row">
            <section className="flex min-w-0 flex-1 flex-col gap-6 px-5 py-7 sm:gap-7 sm:px-14 sm:py-12">
              {headingBlock}
              {conversionBlock}
            </section>

            <AgentSignupPanel email={email} />
          </div>
        ) : (
          <div className="surface-raised flex w-full max-w-[440px] flex-col gap-6 px-5 py-7 sm:px-10 sm:py-8">
            {headingBlock}
            {conversionBlock}
          </div>
        )}
      </main>

    </div>
  )
}

const AGENT_MD_URL = 'https://app.humblytics.com/agent.md'
const AGENT_MARKS = [
  { label: 'Claude', src: claudeMark },
  { label: 'ChatGPT', src: chatgptMark },
  { label: 'Codex', src: codexMark },
  { label: 'Cursor', src: cursorMark },
]

/**
 * Sign-up's right column (Paper "04 Agent flow" → Platform · 01 Sign up, 2026-11-03): the same agent route as
 * the humblytics.com hero, for people who came to the form instead. The prompt picks up the email typed on the
 * left and points the agent at agent.md, which creates the same account. The email stays in the copied text only.
 */
function AgentSignupPanel({ email }: { email: string }) {
  const [copied, setCopied] = useState(false)
  const clean = email.trim()
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)
  const [handoff, setHandoff] = useState<{ code: string; email: string } | null>(null)
  const prompt = `Read ${AGENT_MD_URL} and sign me up for Humblytics${valid ? ` as ${clean}` : ''}.`

  const copy = async () => {
    const code = newHandoffCode()
    const boundEmail = valid ? clean : ''
    try {
      await navigator.clipboard.writeText(`${prompt} Signup code: ${code}`)
    } catch {
      toast.error('Could not copy. Select the prompt and copy it.')
      return
    }
    setHandoff({ code, email: boundEmail })
    void registerHandoff(code, boundEmail)
    track('signup_agent_prompt_copied', null, { ...getAcquisitionContext(), has_email: valid })
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2400)
  }

  return (
    <aside
      className="flex min-w-0 flex-col justify-center gap-5 border-t border-border bg-muted/50 px-5 py-7 sm:px-10 sm:py-12 lg:w-[440px] lg:shrink-0 lg:border-l lg:border-t-0"
      aria-labelledby="auth-agent-heading"
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[13px] font-medium leading-5 text-muted-foreground">Have an AI agent?</p>
          <span className="flex items-center gap-1" aria-hidden="true">
            {AGENT_MARKS.map((m) => <img key={m.label} src={m.src} alt="" title={m.label} className="size-3.5 object-contain" />)}
          </span>
        </div>
        <h2 id="auth-agent-heading" className="text-[22px] font-semibold leading-7 tracking-[-0.015em] text-foreground">
          Sign up from your agent
        </h2>
        <p className="text-[13px] leading-5 text-muted-foreground">
          Paste one prompt into Claude, ChatGPT, Codex or Cursor. It makes your account, adds your site and installs tracking.
        </p>
      </div>

      <div className="flex flex-col gap-3 rounded-xl bg-card p-4 ring-1 ring-black/[0.06]">
        <ol className="flex flex-col gap-3">
          {['Copy the prompt. It uses the email you type.', 'Paste it into your agent.'].map((step, i) => (
            <li key={step} className="flex gap-2.5">
              <span aria-hidden="true" className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-medium tabular-nums text-foreground">{i + 1}</span>
              <span className="text-[13px] leading-5 text-foreground">{step}</span>
            </li>
          ))}
        </ol>
        <p aria-live="polite" className="select-all break-words rounded-lg bg-neutral-50 px-3 py-2.5 text-xs leading-[18px] text-muted-foreground">
          Read {AGENT_MD_URL} and sign me up for Humblytics as{' '}
          <span className={valid ? 'font-medium text-foreground' : 'text-neutral-400'}>{valid ? clean : 'you@example.com'}</span>.
          {handoff && handoff.email === (valid ? clean : '') ? ` Signup code: ${handoff.code}` : null}
        </p>
        <Button type="button" variant="secondary" onClick={copy} className="h-10 w-full gap-2 rounded-lg bg-foreground text-sm font-medium text-background shadow-none hover:bg-foreground/90">
          <HugeiconsIcon icon={copied ? Tick01Icon : Copy01Icon} size={15} />
          {copied ? 'Copied' : 'Copy prompt'}
        </Button>
      </div>

      <p className="text-xs leading-[18px] text-muted-foreground">
        Either way it is the same account. You pick the app or your agent in the next step, and can switch any time.
      </p>
    </aside>
  )
}
